import React, { useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { EditorInput, Button } from "./ScheduleEditorUI";
import { DAYS_OF_WEEK } from "./constants";
import WindowCategoryPicker from "./WindowCategoryPicker";
import ShiftBreaksFields from "./ShiftBreaksFields";

export const newShift = (weekIndex, dayOfWeek, categoryIds) => ({
  weekIndex, dayOfWeek, startTime: "09:00", endTime: "17:00",
  mode: "regular", serviceCategoryIds: [...categoryIds], breaks: [],
});

export function copyWorkingDay(windows, weekIndex, sourceDay, targetDays) {
  const source = windows.filter((item) => Number(item.weekIndex || 0) === weekIndex && item.dayOfWeek === sourceDay);
  const targets = targetDays.filter((day) => day !== sourceDay);
  return [
    ...windows.filter((item) => Number(item.weekIndex || 0) !== weekIndex || !targets.includes(item.dayOfWeek)),
    ...targets.flatMap((dayOfWeek) => source.map((item) => ({
      ...item, dayOfWeek, serviceCategoryIds: [...(item.serviceCategoryIds || [])],
      breaks: (item.breaks || []).map((pause) => ({ ...pause })),
    }))),
  ];
}

const clock = (value) => {
  const [hour, minute] = (value || "00:00").split(":").map(Number);
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
};

export default function WorkWeekEditor({ windows, onChange, cycleWeeks, categoryIds, categoryOptions }) {
  const [selectedWeek, setSelectedWeek] = useState(0);
  const week = Math.min(selectedWeek, Number(cycleWeeks) - 1);
  const [expandedDay, setExpandedDay] = useState("MONDAY");
  const [copyDay, setCopyDay] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [notice, setNotice] = useState("");
  const [offDays, setOffDays] = useState({});
  const forDay = (day) => windows.map((item, index) => ({ ...item, index }))
    .filter((item) => Number(item.weekIndex || 0) === week && item.dayOfWeek === day);
  const update = (index, patch) => onChange(windows.map((item, i) => i === index ? { ...item, ...patch } : item));
  const toggleDay = (day) => {
    const current = forDay(day);
    const key = `${week}-${day}`;
    if (current.length) {
      setOffDays((previous) => ({ ...previous, [key]: current.map(({ index, ...item }) => item) }));
      onChange(windows.filter((item) => Number(item.weekIndex || 0) !== week || item.dayOfWeek !== day));
    } else {
      onChange([...windows, ...(offDays[key] || [newShift(week, day, categoryIds)])]);
      setExpandedDay(day);
    }
    setNotice("");
  };

  return <section className="working-days-editor" aria-label="Working days">
    {Number(cycleWeeks) > 1 && <div className="schedule-segments" aria-label="Cycle week">
      {Array.from({ length: Number(cycleWeeks) }, (_, index) => <button type="button" key={index}
        aria-pressed={week === index} onClick={() => { setSelectedWeek(index); setCopyDay(null); setNotice(""); }}>
        Week {index + 1}
      </button>)}
    </div>}
    <div className="schedule-section-title"><div><h3>Working days</h3></div>
    </div>
    <ToggleButtonGroup className="schedule-day-switches" aria-label="Select working days">
      {DAYS_OF_WEEK.map((day) => <ToggleButton key={day.value} value={day.value} selected={!!forDay(day.value).length} aria-label={day.name}
        aria-pressed={!!forDay(day.value).length} onClick={() => toggleDay(day.value)}>{day.name.slice(0, 3)}</ToggleButton>)}
    </ToggleButtonGroup>
    <p className="schedule-inline-notice" role="status">{notice}</p>
    <div className="schedule-days">
      {DAYS_OF_WEEK.map((day) => {
        const shifts = forDay(day.value);
        if (!shifts.length) return null;
        const expanded = expandedDay === day.value;
        return <section className={`schedule-day ${expanded ? "is-expanded" : ""}`} key={`${week}-${day.value}`}>
          <div className="schedule-day-heading">
            <button type="button" className="schedule-day-disclosure" aria-expanded={expanded}
              aria-controls={`working-${week}-${day.value}`} onClick={() => setExpandedDay(expanded ? null : day.value)}>
              <strong>{day.name}</strong>
              <span>{shifts.map((shift) => `${clock(shift.startTime)} – ${clock(shift.endTime)}`).join(" · ")}</span>
              <span aria-hidden="true">{expanded ? "−" : "+"}</span>
            </button>
            <Button variant="text" type="button" className="schedule-text-button" onClick={() => {
              setCopyDay(copyDay === day.value ? null : day.value); setCopyTargets([]);
            }}>Copy to days</Button>
          </div>
          {copyDay === day.value && <div className="schedule-copy-panel">
            <strong>Copy {day.name.toLowerCase()}’s hours, breaks and appointment types</strong>
            <div className="schedule-copy-targets">{DAYS_OF_WEEK.filter((d) => d.value !== day.value).map((target) =>
              <label key={target.value}><input type="checkbox" checked={copyTargets.includes(target.value)}
                onChange={(event) => setCopyTargets(event.target.checked ? [...copyTargets, target.value] : copyTargets.filter((d) => d !== target.value))} />{target.name}</label>)}</div>
            <div className="schedule-inline-actions"><Button type="button" disabled={!copyTargets.length}
              onClick={() => { onChange(copyWorkingDay(windows, week, day.value, copyTargets)); setCopyDay(null); setNotice(`Copied ${day.name.toLowerCase()} to ${copyTargets.length} ${copyTargets.length === 1 ? "day" : "days"}.`); }}>Apply to selected days</Button>
              <Button variant="text" type="button" className="schedule-text-button" onClick={() => setCopyDay(null)}>Cancel</Button></div>
          </div>}
          <div id={`working-${week}-${day.value}`} hidden={!expanded}>
            {shifts.map((shift, shiftIndex) => <div className="schedule-shift" key={shift.index}>
              <div className="schedule-section-title"><h4>{shifts.length > 1 ? `Working hours · shift ${shiftIndex + 1}` : "Working hours"}</h4>
                {shifts.length > 1 && <Button variant="text" type="button" className="schedule-text-button" aria-label={`Remove ${day.name} shift ${shiftIndex + 1}`}
                  onClick={() => onChange(windows.filter((_, index) => index !== shift.index))}>Remove shift</Button>}</div>
              <div className="schedule-time-range">
                <label>Start time<EditorInput aria-label="Start time" type="time" required value={shift.startTime} onChange={(e) => update(shift.index, { startTime: e.target.value })} /></label>
                <span aria-hidden="true">to</span>
                <label>End time<EditorInput aria-label="End time" type="time" required value={shift.endTime} onChange={(e) => update(shift.index, { endTime: e.target.value })} /></label>
              </div>
              {!!shift.startDayOffset ? <p className="schedule-help">These hours start the following day.</p> : shift.endTime <= shift.startTime && <p className="schedule-help">Ends the following day.</p>}
              <WindowCategoryPicker options={categoryOptions} value={shift.serviceCategoryIds || []}
                onChange={(serviceCategoryIds) => update(shift.index, { serviceCategoryIds })} />
              <ShiftBreaksFields
                shift={shift}
                breaks={shift.breaks || []}
                onChange={(breaks) => update(shift.index, { breaks })}
                removeAriaLabel={(pauseIndex) => `Remove break ${pauseIndex + 1} from ${day.name} shift ${shiftIndex + 1}`}
              />
            </div>)}
            <div className="schedule-day-footer"><Button variant="text" type="button" className="schedule-text-button"
              onClick={() => onChange([...windows, newShift(week, day.value, categoryIds)])}>+ Add another shift</Button>
              <span>Use separate shifts for different appointment types.</span></div>
          </div>
        </section>;
      })}
      {!windows.some((item) => Number(item.weekIndex || 0) === week) && <div className="scheduling-empty">No working days selected for this week. Select a day above to add hours.</div>}
    </div>
  </section>;
}
